import { authorizedFetch } from './session';

/**
 * Stack Tower (C013 Phase 6) leaderboard client — structurally the same as
 * daily-challenge.ts's fetch wrapper. `authorizedFetch` attaches a session
 * token only when one exists, so both calls work signed out (a guest submits
 * under a chosen display name; a signed-in player's real username is used
 * server-side and any client `displayName` is ignored).
 */

/** 'endless' (play-until-miss) or '60s' (C017 Phase 2, spec §98). Omit for 'endless' — matches the server's own default for pre-Phase-2 callers. */
export type StackTowerMode = 'endless' | '60s';

export interface StackTowerSubmitPayload {
  runId: string;
  /** Ignored server-side for a signed-in caller — their username is used instead. */
  displayName?: string;
  score: number;
  maxPerfectStreak: number;
  perfectCount: number;
  finalWidthFraction: number;
  zoneId: string;
  runDurationMs: number;
  mode?: StackTowerMode;
}

export interface StackTowerSubmitResponse {
  ok: true;
  displayName: string;
  score: number;
  /** XP this run earned toward the account level (C014): a tiny participation amount plus a bonus for a new best. 0 for a guest or a deduped retry. */
  xpAwarded: number;
  /** True when the run beat the player's previous best (so the XP included the best-improvement bonus). */
  newBest: boolean;
  deduped?: boolean;
}

export interface StackTowerLeaderboardRow {
  rank: number;
  displayName: string;
  score: number;
  maxPerfectStreak: number;
  zoneId: string | null;
  createdAt: number;
}

export interface StackTowerLeaderboardResponse {
  leaderboard: StackTowerLeaderboardRow[];
}

export function submitStackTowerRun(payload: StackTowerSubmitPayload): Promise<StackTowerSubmitResponse> {
  return authorizedFetch<StackTowerSubmitResponse>('/api/stack-tower/submit', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function fetchStackTowerLeaderboard(mode: StackTowerMode = 'endless'): Promise<StackTowerLeaderboardResponse> {
  return authorizedFetch<StackTowerLeaderboardResponse>(`/api/stack-tower/leaderboard?mode=${mode}`);
}

export interface StackTowerStats {
  totalRuns: number;
  bestScore: number;
  totalPerfects: number;
  bestPerfectStreak: number;
  totalBlocks: number;
  averageScore: number;
}

export interface StackTowerStatsResponse {
  stats: StackTowerStats;
}

/** The signed-in player's own Stack Tower aggregates. Rejects (401) for a guest — callers catch and skip. */
export function fetchStackTowerStats(): Promise<StackTowerStatsResponse> {
  return authorizedFetch<StackTowerStatsResponse>('/api/stack-tower/stats');
}

export interface StackTowerMulti60LeaderboardRow {
  rank: number;
  username: string;
  score: number;
}

export interface StackTowerMulti60LeaderboardResponse {
  leaderboard: StackTowerMulti60LeaderboardRow[];
}

/** A raw high-score board for 60-second head-to-head matches (C017 Phase 3, spec §115) — separate from the ranked Elo ladder, which orders by rating. */
export function fetchStackTowerMulti60Leaderboard(): Promise<StackTowerMulti60LeaderboardResponse> {
  return authorizedFetch<StackTowerMulti60LeaderboardResponse>('/api/stack-tower/multi60/leaderboard');
}
